import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { errorText, useToast } from './Toast';

function initials(first: string, last: string): string {
  const a = (first || '').trim().charAt(0);
  const b = (last || '').trim().charAt(0);
  return `${a}${b}`.toUpperCase() || '?';
}

const MENU_WIDTH_PX = 256;
const MENU_GAP_PX = 8;
const VIEWPORT_PAD_PX = 8;

export function AccountMenu() {
  const { user, logout } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }

    function placeMenu() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = Math.min(MENU_WIDTH_PX, window.innerWidth - VIEWPORT_PAD_PX * 2);
      let left = rect.right - width;
      left = Math.max(
        VIEWPORT_PAD_PX,
        Math.min(left, window.innerWidth - width - VIEWPORT_PAD_PX),
      );
      setMenuPos({ top: rect.bottom + MENU_GAP_PX, left, width });
    }

    placeMenu();
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const items = () =>
      Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') || []);

    const focusItem = (index: number) => {
      const list = items();
      if (!list.length) return;
      const next = ((index % list.length) + list.length) % list.length;
      list[next]?.focus();
    };

    focusItem(0);

    function onDoc(event: MouseEvent) {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      const list = items();
      if (!list.length) return;
      const current = list.indexOf(document.activeElement as HTMLElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        focusItem(current < 0 ? 0 : current + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        focusItem(current < 0 ? list.length - 1 : current - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        focusItem(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        focusItem(list.length - 1);
      }
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const accountLabel = `Account menu for ${user.first_name} ${user.last_name}`;

  return (
    <div className="relative ml-2" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="inline-flex min-h-11 items-center gap-2 px-2 py-1.5 text-sm text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-oxide-700"
        aria-label={accountLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        <span
          className="inline-flex h-8 w-8 items-center justify-center bg-oxide-700 text-xs font-bold uppercase text-white"
          aria-hidden="true"
        >
          {initials(user.first_name, user.last_name)}
        </span>
        <span className="hidden max-w-[10rem] truncate text-left sm:block">
          <span className="block font-medium leading-tight">
            {user.first_name} {user.last_name}
          </span>
          <span className="block text-xs capitalize text-ink-300">{user.role}</span>
        </span>
        <span className="text-ink-400" aria-hidden="true">
          ▾
        </span>
      </button>

      {open &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Account menu"
            className="fixed z-[60] overflow-hidden border border-ink-100 bg-panel py-1 text-ink-900 shadow-lg"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            <div className="border-b border-ink-100 px-3 py-2" role="none">
              <p className="truncate text-sm font-semibold text-ink-950">
                {user.first_name} {user.last_name}
              </p>
              <p className="truncate text-xs text-ink-600">{user.email}</p>
              <p className="mt-0.5 text-xs capitalize text-ink-600">{user.role}</p>
            </div>
            <Link
              role="menuitem"
              to="/settings"
              className="account-menu-link"
              onClick={() => setOpen(false)}
            >
              Settings
            </Link>
            <div className="my-1 border-t border-ink-100" role="separator" />
            <button
              type="button"
              role="menuitem"
              className="account-menu-link w-full text-left text-signal-danger"
              onClick={() => {
                setOpen(false);
                void logout()
                  .then(() => {
                    toast.success('Signed out');
                    navigate('/login');
                  })
                  .catch((err: unknown) =>
                    toast.error('Could not sign out', errorText(err, 'Sign out failed')),
                  );
              }}
            >
              Sign out
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
}
